const ACTION_MAP = { 958460001: 'Show', 958460000: 'Hide', 958460004: 'Go To', 958460005: 'Populate From' };
const OPERATOR_MAP = { 958460000: '==', 958460001: '!=', 958460002: 'contains', 477780001: '>', 477780002: '<' };

const getSurveyOperator = (op) => {
    if (!op) return null;
    switch (op) {
        case '==': return '=';
        case '!=': return '!=';
        case 'contains': return 'contains';
        case '>': return '>';
        case '<': return '<';
        default: console.warn(`Unsupported operator received: ${op}`); return null;
    }
};

const getInverseSurveyOperator = (op) => {
    if (!op) return null;
    switch (op) {
        case '==': return '!=';
        case '!=': return '=';
        case 'contains': return 'notcontains';
        case '>': return '<=';
        case '<': return '>=';
        default: console.warn(`Cannot determine inverse for unsupported operator: ${op}`); return null;
    }
};


const appendExpression = (existingExpression, newExpression, logic = 'or') => {
    if (!existingExpression) {
        return newExpression;
    }
    return `${existingExpression} ${logic} ${newExpression}`;
};

// Field type templates keyed by questionType.dataType - applied to a newQuestion object
const QUESTION_TYPE_BUILDERS = {
    "DropDown": (newQuestion) => { newQuestion.type = "dropdown"; newQuestion.choices = []; },
    "Multi Select": (newQuestion) => { newQuestion.type = "checkbox"; newQuestion.choices = []; newQuestion.showSelectAllItem = true; },
    "Numeric": (newQuestion) => { newQuestion.type = "text"; newQuestion.inputType = "number"; newQuestion.min = 0; },
    "Date": (newQuestion) => { newQuestion.type = "text"; newQuestion.inputType = "date"; },
    "Text": (newQuestion) => { newQuestion.type = "text"; },
    "Text Area": (newQuestion) => { newQuestion.type = "comment"; },
    "Email": (newQuestion) => {
        newQuestion.type = "text";
        newQuestion.inputType = "email";
        newQuestion.validators = [{ type: "email" }];
    },
    "Phone": (newQuestion) => {
        newQuestion.type = "text";
        newQuestion.inputType = "tel";
        newQuestion.validators = [{
            type: "regex",
            regex: String.raw`^[\d\s\+\-()]*$`,
            text: "Please enter a valid phone number"
        }];
    },
    "Web": (newQuestion) => {
        newQuestion.type = "text";
        newQuestion.inputType = "url";
        newQuestion.validators = [{
            type: "regex",
            regex: String.raw`^(https?:\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$`,
            text: "Please enter a valid URL (e.g., https://example.com or google.com)"
        }];
    },
    "Table": (newQuestion) => {
        newQuestion.type = "paneldynamic";
        newQuestion.panelCount = 0;
        newQuestion.allowAddPanel = true;
        newQuestion.addPanelText = "Add New Address";
        newQuestion.templateTitle = "Location #{panelIndex}";
        newQuestion.templateElements = [
            { type: "boolean", name: "isPrimary", title: "Primary Location", isRequired: true },
            { type: "text", name: "name", title: "Name/Address", isRequired: true, width: "20%" },
            { type: "text", name: "town_city", title: "Town/City", isRequired: true, startWithNewLine: false, width: "20%" },
            { type: "text", name: "county", title: "County", startWithNewLine: false, width: "20%" },
            { type: "text", name: "postcode", title: "Postcode", isRequired: true, startWithNewLine: false, width: "20%" },
            { type: "text", name: "country", title: "Country", isRequired: true, startWithNewLine: false, width: "20%" },
            { type: "text", inputType: "number", name: "employeeCount", title: "How many people work at this location?", isRequired: true },
            { type: "comment", name: "processesNotes", title: "What are the key processes performed at this location?", isRequired: true, maxLength: 200 }
        ];
    },
};

/**
 * Builds a single SurveyJS question definition from a source question, or null if its type is unsupported.
 */
const buildSurveyQuestion = (q, includeVersionBadges) => {
    const newQuestion = {
        name: q.questionId,
        title: formatQuestionTitle(q.question, q.version, includeVersionBadges),
        description: q.tooltip,
        isRequired: q.isRequired,
        visible: !q.isHidden,
        order: q.order,
    };

    if (q.maxFieldLength && q.maxFieldLength > 0) {
        newQuestion.maxLength = q.maxFieldLength;
    }

    const applyType = QUESTION_TYPE_BUILDERS[q.questionType.dataType];
    if (!applyType) {
        console.warn(`Unknown type: ${q.questionType.dataType}`);
        return null;
    }
    applyType(newQuestion);

    if (q.options && q.options.length > 0) {
        newQuestion.choices = q.options.map(option => ({
            text: option.optionText,
            value: option.optionText,
            order: option.optionOrder
        }));
    }

    return newQuestion;
};

/**
 * Builds a single SurveyJS page definition from a source group container.
 */
const buildSurveyPage = (groupContainer, includeVersionBadges) => {
    const group = groupContainer.questionGroups;
    const groupId = group.questions.length > 0 ? group.questions[0].questionGroupId : group.groupName;
    const surveyQuestions = group.questions
        .map(q => buildSurveyQuestion(q, includeVersionBadges))
        .filter(Boolean);

    return {
        groupId: groupId,
        name: group.groupName,
        title: formatPageTitle(group.groupName, group.version, includeVersionBadges),
        description: group.tabInfo || '',
        visible: !group.groupIsHidden,
        order: group.groupOrder,
        elements: surveyQuestions,
        isProduct: group.isProduct,
    };
};

/**
 * Applies a "Show"/"Hide" visibility rule's expression to the given visibleIf-capable target.
 */
const applyVisibilityExpression = (target, ruleContext, warnLabel) => {
    const { action, operator, sourceQuestionId, surveyValue } = ruleContext;
    const surveyOp = (action === 'Show') ? getSurveyOperator(operator) : getInverseSurveyOperator(operator);
    if (!surveyOp) {
        console.warn(`Unsupported operator "${operator}" in ${warnLabel}:`, { action, operator });
        return;
    }
    const expression = `{${sourceQuestionId}} ${surveyOp} ${surveyValue}`;
    target.visibleIf = appendExpression(target.visibleIf, expression, 'or');
};

/**
 * Handles a single rule whose target is a page (targetType === true).
 */
const applyPageRule = (rule, ruleContext, pageMap) => {
    if (ruleContext.action !== 'Show' && ruleContext.action !== 'Hide') return;

    const targetPage = pageMap.get(rule.targetGroupId);
    if (!targetPage) {
        console.warn('Rule target page not found:', rule.targetGroupId);
        return;
    }
    applyVisibilityExpression(targetPage, ruleContext, 'page rule');
};

/**
 * Handles a single rule whose target is a question (targetType === false).
 */
const applyQuestionRule = (rule, ruleContext, questionMap, dynamicPanelLinks, surveyTriggers) => {
    if (!rule.targetQuestionId) return;

    const { action, operator, sourceQuestionId, surveyValue } = ruleContext;
    const targetQuestion = questionMap.get(rule.targetQuestionId);
    if (!targetQuestion) {
        console.warn('Rule target question not found:', rule.targetQuestionId);
        return;
    }

    if (action === 'Populate From') {
        if (targetQuestion.type === 'paneldynamic') {
            targetQuestion.panelCount = `{${sourceQuestionId}}`;
            targetQuestion.allowAddPanel = false;
            targetQuestion.allowRemovePanel = false;
            delete targetQuestion.addPanelText;
            dynamicPanelLinks.push({ source: sourceQuestionId, target: targetQuestion.name });
        } else {
            console.warn('"Populate From" rule targets a non-paneldynamic question:', rule);
        }
        return;
    }

    if (action === 'Show' || action === 'Hide') {
        if (!operator) {
            console.warn('Skipping invalid visibility rule:', rule);
            return;
        }
        applyVisibilityExpression(targetQuestion, ruleContext, 'question rule');
        return;
    }

    if (action === 'Go To') {
        if (!operator) {
            console.warn('Skipping invalid Go To rule:', rule);
            return;
        }
        const surveyOp = getSurveyOperator(operator);
        if (surveyOp) {
            surveyTriggers.push({
                type: "runExpression",
                expression: `{${sourceQuestionId}} ${surveyOp} ${surveyValue}`,
                runExpression: `survey.navigateToQuestion('${rule.targetQuestionId}')`
            });
        }
    }
};

/**
 * Applies a single rule (page or question target) from a source question.
 */
const applyRule = (rule, sourceQuestionId, pageMap, questionMap, dynamicPanelLinks, surveyTriggers) => {
    if (rule.isActive === false) return;

    const action = ACTION_MAP[rule.action];
    const operator = OPERATOR_MAP[rule.targetOperator];
    const value = rule.targetValue;
    const surveyValue = typeof value === 'string' ? `'${value}'` : value;
    const ruleContext = { action, operator, sourceQuestionId, surveyValue };

    if (rule.targetType === true) {
        applyPageRule(rule, ruleContext, pageMap);
    } else if (rule.targetType === false) {
        applyQuestionRule(rule, ruleContext, questionMap, dynamicPanelLinks, surveyTriggers);
    }
};

/**
 * Walks all source questions' rules and applies each to the relevant page/question maps.
 */
const applyAllRules = (questionGroups, pageMap, questionMap, dynamicPanelLinks, surveyTriggers) => {
    questionGroups.forEach(groupContainer => {
        groupContainer.questionGroups.questions.forEach(sourceQuestion => {
            if (!sourceQuestion.rules || sourceQuestion.rules.length === 0) return;
            sourceQuestion.rules.forEach(rule =>
                applyRule(rule, sourceQuestion.questionId, pageMap, questionMap, dynamicPanelLinks, surveyTriggers)
            );
        });
    });
};

/**
 * Transform API response data to SurveyJS JSON format
 * @param {object} apiResponse - The API response containing Questions array
 * @param {object} options - Transformation options
 * @param {boolean} options.includeVersionBadges - Whether to show version badges on questions (default: false)
 * @returns {object} SurveyJS compatible JSON schema
 */
export function transformDataToSurveyJSON(apiResponse, options = {}) {
    const { includeVersionBadges = false } = options;
    if (!apiResponse || !apiResponse.Questions) {
        console.error("Invalid API response structure received in transformer.");
        return { pages: [] };
    }

    const dynamicPanelLinks = [];
    const surveyTriggers = [];

    const pages = apiResponse.Questions.map(groupContainer => buildSurveyPage(groupContainer, includeVersionBadges));

    const questionMap = new Map();
    const pageMap = new Map();
    pages.forEach(page => {
        pageMap.set(page.groupId, page);
        page.elements.forEach(q => questionMap.set(q.name, q));
    });

    applyAllRules(apiResponse.Questions, pageMap, questionMap, dynamicPanelLinks, surveyTriggers);

    pages.forEach((page) => {
        // Sort questions within the page
        page.elements.sort((a, b) => a.order - b.order);
        // Sort choices within each question
        page.elements.forEach((element) => {
            if (element.choices) element.choices.sort((a, b) => a.order - b.order);
        });
    });

    pages.sort((a, b) => a.order - b.order);

    return {
        showTOC: true,
        tocLocation: 'left',
        showPreviewBeforeComplete: true,
        showQuestionNumbers: "on",
        widthMode: "responsive",
        dynamicPanelLinks: dynamicPanelLinks,
        triggers: surveyTriggers,
        pages,
    };
}

/**
 * Formats the question title with an optional version badge
 * @param {string} questionText - The original question text
 * @param {object} version - Version info object with versionId, versionName, VersionNumber
 * @param {boolean} includeVersionBadge - Whether to include the version badge
 * @returns {string|object} The formatted title (plain text or object with html property)
 */
const formatQuestionTitle = (questionText, version, includeVersionBadge) => {
    if (!includeVersionBadge || !version || !version.VersionNumber) {
        return questionText;
    }
    
    // Determine badge color based on version name (draft vs published)
    const isDraft = version.versionName?.toLowerCase().includes('draft');
    const badgeClass = isDraft ? 'version-badge-draft' : 'version-badge-published';
    
    // Return plain string with HTML - SurveyJS will render it when we set survey.onTextMarkdown
    return `${questionText} <span class="version-badge ${badgeClass}">v${version.VersionNumber}</span>`;
};

/**
 * Formats the page/group title with an optional version badge
 * @param {string} groupName - The original group name
 * @param {object} version - Version info object
 * @param {boolean} includeVersionBadge - Whether to include the version badge
 * @returns {string} The formatted title
 */
const formatPageTitle = (groupName, version, includeVersionBadge) => {
    if (!includeVersionBadge || !version || !version.VersionNumber) {
        return groupName;
    }
    
    const isDraft = version.versionName?.toLowerCase().includes('draft');
    const badgeClass = isDraft ? 'version-badge-draft' : 'version-badge-published';
    
    // Return plain string with HTML - SurveyJS will render it when we set survey.onTextMarkdown
    return `${groupName} <span class="version-badge ${badgeClass}">v${version.VersionNumber}</span>`;
};